import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import { ApiService, Store } from 'src/data/services/Services';
import errorHandler from 'src/core/utils/errorHandler';
import Translations from 'src/core/utils/Translations';
import type { ApiPage } from 'src/core/models/api/ApiPage';
import type { ApiTimetableDetailItem } from 'src/core/models/api/shared/ApiTimetable';

export interface BarTimetableItem {
	item: ApiTimetableDetailItem;
	eventUuid: string;
	key: string;
}

interface MyBarTimeTableState {
	myTimeTableData: BarTimetableItem[];
	isLoading: boolean;
	// True when the server favourites could not be read.
	loadFailed: boolean;
}

const initialState: MyBarTimeTableState = {
	myTimeTableData: [],
	isLoading: true,
	loadFailed: false,
};

/**
 * The key a session is tracked under: its uid, or its times for a session that predates them.
 * Not exported on purpose: rebuilding this key elsewhere left the heart empty (#402/#404).
 */
function keyFor(item: ApiTimetableDetailItem): string {
	if (item.uid) return item.uid;
	return String(item.startdate) + String(item.title) + String(item.enddate) + String(item.eventUuid);
}

/** Every session of an event as uid => session, read from the pages the app already holds. */
function sessionsByUid(pages: ApiPage[], eventUuid: string): Map<string, ApiTimetableDetailItem> {
	const sessions = new Map<string, ApiTimetableDetailItem>();

	const walk = (list: ApiPage[]) => {
		list.forEach((page) => {
			if (page.type === 'TimetableDay') {
				const blocks = page.data?.timetable ?? [];
				blocks.forEach((block) => {
					(block.items ?? []).forEach((item) => {
						if (item.uid) {
							sessions.set(item.uid, {
								...item,
								stageTitle: block.title ?? '',
								navTitle: page.name,
								eventUuid,
							});
						}
					});
				});
			}
			walk((page.children ?? []) as ApiPage[]);
		});
	};

	walk(pages);

	return sessions;
}

export const useMyBarTimeTableStore = create(
	combine(initialState, (set, get) => ({
		init: async () => {
			const saved = await Store.getObject<BarTimetableItem[]>('myBarTimetable');
			set({ myTimeTableData: saved ?? [], isLoading: false });
		},

		// Optimistic local update for a snappy heart, then the server (source of truth) and a roll-back when it
		// refuses.
		toggleFavorite: async (item: ApiTimetableDetailItem, eventUuid: string) => {
			const key = keyFor(item);
			const previous = [...get().myTimeTableData];
			const existing = previous.find((i) => i.key === key);

			const updated = existing ? previous.filter((i) => i.key !== key) : [...previous, { item, eventUuid, key }];

			set({ myTimeTableData: updated });
			Store.save('myBarTimetable', updated);

			if (!item.uid) return;

			try {
				const { data } = await ApiService.endpoints.favorites.toggleSession({ eventUuid, uid: item.uid });
				const onServer = data?.is_favorite ?? !existing;

				// Reconcile: the server decides, so a race or a stale toggle cannot leave the heart saying something the
				// server does not.
				const reconciled = onServer
					? [...get().myTimeTableData.filter((i) => i.key !== key), { item, eventUuid, key }]
					: get().myTimeTableData.filter((i) => i.key !== key);

				set({ myTimeTableData: reconciled });
				Store.save('myBarTimetable', reconciled);
			} catch {
				set({ myTimeTableData: previous });
				Store.save('myBarTimetable', previous);
				errorHandler(Translations.errorDefaultSubject, Translations.errorFavoriteToggleFailed);
			}
		},

		// Make the server's list leading for this event, resolving each uid back to the session it belongs to.
		syncFromServer: async (eventUuid: string, pages: ApiPage[]) => {
			if (!eventUuid) return;

			try {
				const { data } = await ApiService.endpoints.favorites.list({ eventUuid });

				if (!data || !Array.isArray(data.session_uids)) return;

				const known = sessionsByUid(pages, eventUuid);
				const fromServer: BarTimetableItem[] = [];

				data.session_uids.forEach((uid) => {
					const item = known.get(uid);
					if (item) fromServer.push({ item, eventUuid, key: uid });
				});

				// The event's pages may not be loaded yet, in which case no uid can be resolved.
				if (data.session_uids.length > 0 && fromServer.length === 0) {
					set({ loadFailed: false });
					return;
				}

				const others = get().myTimeTableData.filter(
					// Entries from another event, plus this event's sessions from before they carried a uid: the server
					// knows nothing about those.
					(entry) => entry.eventUuid !== eventUuid || !entry.item.uid,
				);

				const merged = [...others, ...fromServer];

				set({ myTimeTableData: merged, loadFailed: false });
				Store.save('myBarTimetable', merged);
			} catch {
				if (!get().loadFailed) {
					set({ loadFailed: true });
				}
			}
		},

		// Takes the session itself, not a key: only the store knows how a session is identified, and that changed
		// once already (times, then uid).
		isFavorite: (item: ApiTimetableDetailItem) => {
			const key = keyFor(item);

			return get().myTimeTableData.some((entry) => entry.key === key);
		},
	})),
);
